import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCard, Globe, AlertTriangle, ChevronLeft, ChevronRight, RefreshCcw, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminCheckoutFailures() {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [countryFilter, setCountryFilter] = useState('');
  const [countrySummary, setCountrySummary] = useState([]);

  useEffect(() => {
    loadFailures();
  }, [page]);

  const loadFailures = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/checkout-failures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allFailures = res.data.failures || [];
      setFailures(allFailures);
      
      // Calculate country summary
      const countryMap = {};
      allFailures.forEach(f => {
        const country = f.country || 'Unknown';
        countryMap[country] = (countryMap[country] || 0) + 1;
      });
      const summary = Object.entries(countryMap)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count);
      setCountrySummary(summary);
      
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Failed to load checkout failures:', error);
      toast.error('Failed to load checkout failures');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getErrorBadge = (errorCode) => {
    const badges = {
      'card_declined': { label: 'Card Declined', color: 'bg-red-500/20 text-red-400' },
      'insufficient_funds': { label: 'Insufficient Funds', color: 'bg-orange-500/20 text-orange-400' },
      'expired_card': { label: 'Expired Card', color: 'bg-yellow-500/20 text-yellow-400' },
      'processing_error': { label: 'Processing Error', color: 'bg-blue-500/20 text-blue-400' },
      'authentication_required': { label: 'Auth Required', color: 'bg-[#00F0FF]/20 text-[#00F0FF]' },
    };
    return badges[errorCode] || { label: errorCode || 'Error', color: 'bg-neutral-500/20 text-neutral-400' };
  };

  const filteredFailures = countryFilter 
    ? failures.filter(f => (f.country || 'Unknown').toLowerCase().includes(countryFilter.toLowerCase()))
    : failures;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <CreditCard className="w-8 h-8 text-red-500" />
              Checkout Failures
            </h1>
            <p className="text-neutral-400 mt-1">Monitor failed payment attempts by region</p>
          </div>
          
          <Button
            onClick={loadFailures}
            variant="outline"
            className="border-white/20"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Country Summary */}
        {countrySummary.length > 0 && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-[#00F0FF]" />
              <span className="font-semibold">Failures by Country</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {countrySummary.slice(0, 10).map((item) => (
                <button
                  key={item.country}
                  onClick={() => setCountryFilter(item.country === countryFilter ? '' : item.country)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    countryFilter === item.country
                      ? 'bg-[#00F0FF] text-black'
                      : 'bg-black/30 hover:bg-white/10'
                  }`}
                >
                  {item.country}: {item.count}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Filter by country..."
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="max-w-xs bg-neutral-900/50 border-white/10"
          />
          {countryFilter && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCountryFilter('')}
              className="text-neutral-400"
            >
              Clear
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : filteredFailures.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No checkout failures found</p>
          </div>
        ) : (
          <>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Time</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Country</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Currency</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Error</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Message</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Session ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredFailures.map((failure, idx) => {
                      const errorBadge = getErrorBadge(failure.error_code);
                      // Stable composite key — backend may not return a unique id
                      // for failures, so we combine session id + creation time +
                      // index. Avoids React reconciliation thrash on filter/refresh.
                      const rowKey = `${failure.checkout_session_id || failure.payment_intent_id || 'noid'}-${failure.created_at || ''}-${idx}`;
                      return (
                        <tr key={rowKey} className="hover:bg-white/5">
                          <td className="p-4 text-sm text-neutral-400 whitespace-nowrap">
                            {formatDate(failure.created_at)}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-neutral-400" />
                              <span>{failure.country || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm">
                            {failure.currency?.toUpperCase() || 'USD'}
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${errorBadge.color}`}>
                              {errorBadge.label}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-neutral-400 max-w-xs truncate">
                            {failure.error_message || failure.message || 'N/A'}
                          </td>
                          <td className="p-4 font-mono text-xs text-neutral-500">
                            {failure.checkout_session_id?.slice(0, 20) || failure.payment_intent_id?.slice(0, 20) || 'N/A'}...
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-red-400">{filteredFailures.length}</div>
                <div className="text-sm text-neutral-400">Total Failures</div>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#00F0FF]">{countrySummary.length}</div>
                <div className="text-sm text-neutral-400">Countries Affected</div>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-yellow-400">
                  {filteredFailures.filter(f => f.error_code === 'card_declined').length}
                </div>
                <div className="text-sm text-neutral-400">Card Declined</div>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#00F0FF]">
                  {filteredFailures.filter(f => f.error_code === 'authentication_required').length}
                </div>
                <div className="text-sm text-neutral-400">Auth Required</div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminCheckoutFailures;
